"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  CreditCard,
  FileSearch,
  LayoutDashboard,
  LogOut,
  Settings,
} from "lucide-react";
import { clearToken, getToken } from "@/lib/api";

const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

export type SiteVisitor = {
  email: string;
  name: string | null;
  picture_url: string | null;
  package_name?: string | null;
  plan_name: string | null;
};

// The stored sign-in, readable during render. Changes arrive from other tabs
// (the browser's "storage" event) and from signing out here.
const listeners = new Set<() => void>();
function subscribe(fn: () => void) {
  listeners.add(fn);
  window.addEventListener("storage", fn);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", fn);
  };
}

/**
 * Who is looking at the public website.
 *   null      — nobody is signed in (also what the server renders)
 *   "pending" — a sign-in is stored and is being confirmed
 *   object    — signed in
 * It asks the API directly rather than through the dashboard's request
 * helper, so nothing on a public page can ever redirect a visitor.
 */
// The last confirmed visitor, kept in this browser so a reload shows the
// avatar and name straight away instead of waiting for the API. The token
// stays the real check; this is only what is painted while it is confirmed.
const CACHE_KEY = "rb_site_visitor";

function readCached(): SiteVisitor | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v.email === "string" ? (v as SiteVisitor) : null;
  } catch {
    return null;
  }
}

function writeCached(v: SiteVisitor | null) {
  try {
    if (v)
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          email: v.email,
          name: v.name,
          picture_url: v.picture_url,
          package_name: v.package_name ?? null,
          plan_name: v.plan_name,
        }),
      );
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* storage blocked: the menu just waits for the API */
  }
}

/** Mirrors "a sign-in is stored" onto <html data-auth>. The script in the
 *  root layout sets it before the first paint, so the CSS in globals.css
 *  can hide the Log in / Sign up buttons before React has even loaded. */
function markSignedIn(on: boolean) {
  try {
    if (on) document.documentElement.dataset.auth = "1";
    else delete document.documentElement.dataset.auth;
  } catch {
    /* no document (server) */
  }
}

export function useSiteVisitor() {
  const token = useSyncExternalStore(subscribe, getToken, () => null);
  // What the API said about one particular token; ignored once the token changes.
  const [answer, setAnswer] = useState<{
    token: string;
    me: SiteVisitor | null;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      // No sign-in stored (signed out here, in another tab, or in the dashboard): the guest buttons must show.
      markSignedIn(false);
      return;
    }
    let alive = true;
    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? (res.json() as Promise<SiteVisitor>) : null))
      // An expired sign-in, or an API that can't be reached, shows the sign-in buttons.
      .catch(() => null)
      .then((me) => {
        if (!alive) return;
        writeCached(me);
        markSignedIn(Boolean(me));
        setAnswer({ token, me });
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const signOut = useCallback(() => {
    clearToken();
    writeCached(null);
    markSignedIn(false);
    listeners.forEach((fn) => fn());
  }, []);

  const confirmed: SiteVisitor | "pending" | null = !token
    ? null
    : answer?.token === token
      ? answer.me
      : "pending";
  // While the API confirms a stored sign-in, show who it was last time.
  const visitor: SiteVisitor | "pending" | null =
    confirmed === "pending" ? (readCached() ?? "pending") : confirmed;
  return { visitor, signOut };
}

export const ACCOUNT_LINKS = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/dashboard/check", label: "New check", icon: FileSearch },
  { href: "/dashboard/billing", label: "Plan & billing", icon: CreditCard },
  { href: "/dashboard/settings", label: "Settings", icon: Settings },
];

/** The visitor's picture or initials. "sm" fits the top bar, "md" the phone menus. */
export function Avatar({
  visitor,
  size = "sm",
}: {
  visitor: SiteVisitor | "pending";
  size?: "xs" | "sm" | "md";
}) {
  const box =
    size === "md"
      ? "h-11 w-11 text-[0.85rem]"
      : size === "xs"
        ? "h-6 w-6 text-[0.6rem]"
        : "h-7 w-7 text-[0.66rem] lg:h-8 lg:w-8 lg:text-[0.7rem]";
  if (visitor !== "pending" && visitor.picture_url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={visitor.picture_url}
        alt=""
        className={`${box} block aspect-square max-w-none flex-shrink-0 rounded-full bg-white object-cover`}
        referrerPolicy="no-referrer"
      />
    );
  }
  const initials =
    visitor === "pending"
      ? ""
      : (visitor.name || visitor.email || "?").slice(0, 2).toUpperCase();
  return (
    <span className={`${box} flex aspect-square flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep font-extrabold text-white`}>
      {initials || (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle
            cx="12"
            cy="8"
            r="4"
            stroke="currentColor"
            strokeWidth="2.2"
          />
          <path
            d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
}

/**
 * The profile button in the website's top bar for someone who is signed in.
 * The menu opens on hover with a mouse, and on click or keyboard focus for
 * everyone else. It closes on Escape, on a click outside, or shortly after
 * the pointer leaves.
 */
export function NavAccount({
  visitor,
  onSignOut,
}: {
  visitor: SiteVisitor | "pending";
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closing = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closing.current) clearTimeout(closing.current);
    closing.current = null;
  };
  const closeSoon = () => {
    cancelClose();
    closing.current = setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      if (closing.current) clearTimeout(closing.current);
    };
  }, []);

  const known = visitor === "pending" ? null : visitor;
  const packageName = known?.package_name ?? known?.plan_name ?? null;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={(e) => {
        // Touch screens fire a mouse-enter on tap; let the click handle those.
        if (
          window.matchMedia("(hover: hover)").matches &&
          e.nativeEvent instanceof MouseEvent
        ) {
          cancelClose();
          setOpen(true);
        }
      }}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        onClick={() => {
          // With a mouse the menu is already open from hovering, so a click
          // must not close it again. On touch screens a tap opens and closes.
          cancelClose();
          setOpen((v) =>
            window.matchMedia("(hover: hover)").matches ? true : !v,
          );
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          known
            ? `Account menu for ${known.name || known.email}`
            : "Account menu"
        }
        className="flex items-center gap-1 rounded-full border border-violet-light bg-white py-[3px] pl-[3px] pr-2 shadow-[0_8px_22px_-14px_rgba(143,103,255,0.5)] transition-shadow hover:shadow-[0_14px_30px_-14px_rgba(143,103,255,0.7)]"
      >
        <Avatar visitor={visitor} />
        {known && (
          <span className="hidden max-w-[96px] truncate pl-0.5 text-[0.8rem] font-bold text-ink sm:block lg:max-w-[120px]">
            {(known.name || known.email).split(" ")[0]}
          </span>
        )}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          className={`text-ink-soft transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        // The top padding bridges the gap to the button, so the pointer can travel into the menu.
        <div className="absolute right-0 top-full z-40 w-[264px] pt-2.5">
          <div className="nav-panel overflow-hidden rounded-[24px]" role="menu">
            <div
              className="h-1 bg-gradient-to-r from-violet-deep via-violet to-pink"
              aria-hidden
            />
            <div className="p-2">
              <div className="rounded-[18px] bg-gradient-to-br from-violet-pale via-white to-[#FFF1F8] px-3.5 pb-3 pt-2.5">
                <div className="truncate text-[0.92rem] font-extrabold">
                  {known ? known.name || "Your account" : "Signing you in…"}
                </div>
                {known && (
                  <div className="truncate text-[0.78rem] text-ink-soft">
                    {known.email}
                  </div>
                )}
                {packageName && (
                  <span className="mt-2 inline-flex rounded-full bg-violet-pale px-2.5 py-0.5 text-[0.7rem] font-extrabold text-violet-deep">
                    {packageName}
                  </span>
                )}
              </div>
              <div className="pt-1.5">
                {ACCOUNT_LINKS.map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 rounded-2xl px-2.5 py-2 text-[0.88rem] font-semibold text-ink hover:bg-violet-pale/60"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-pale text-violet-deep">
                      <l.icon size={15} />
                    </span>
                    {l.label}
                  </Link>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSignOut();
                  }}
                  className="mt-1 flex w-full items-center gap-3 rounded-2xl border-t border-line px-3.5 py-2.5 text-left text-[0.88rem] font-semibold text-warn hover:bg-warn-pale"
                >
                  <LogOut size={16} />
                  Log out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
